import { Box, Button, FormControl, FormGroup, FormHelperText, Modal, Select, Stack, TextField, Typography } from "@mui/material";
import { CategoryData, ProductData, ProductDataError } from "../../types";
import { useAPI } from "../../hooks/useAPI";
import { useEffect, useState } from "react";
import { ConfirmDialog } from "../../components/global/ConfirmDialog";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";

interface ProductModalProps {
  open: boolean;
  handleClose:() => void;
  handleSave:() => void;
  product: ProductData;
  categories: CategoryData[];
}

interface CategorySelectOptionProps {
  item: any;
  depth: number;
}

const CategorySelectOption = ({ item, depth }: CategorySelectOptionProps) => {
  const prespace = [];
  for (let i=0; i<depth; i++) prespace.push(i);
  if (item?.children?.length) {
    return (
      <>
        <option value={item?.id}>
          {
            prespace.map(() => <>&nbsp;&nbsp;&nbsp;&nbsp;</>)
          }
          {item?.name}
        </option>
        {
          item?.children?.map((child: any, index: number) => (
            <>
              <CategorySelectOption key={index} item={child} depth={depth + 1} />
            </>
          ))
        }
      </>
    )
  } else {
    return (
      <>
        <option value={item?.id}>
          {
            prespace.map(() => <>&nbsp;&nbsp;&nbsp;&nbsp;</>)
          }
          {item?.name}
        </option>
      </>
    )
  }
}

export const ProductModal = ({ open, handleClose, handleSave, product, categories }: ProductModalProps) => {
  const [mProduct, setMProduct] = useState<ProductData>(product);
  const [error, setError] = useState<ProductDataError>({});
  const { fetchData } = useAPI();
  const { isOpen, config, openDialog, closeDialog } = useConfirmDialog();

  useEffect(() => {
    setMProduct({ ...product});
  }, [product]);

  const onSaveSector = async (sector: ProductData) => {
    const {errors, data} = await fetchData('/products/save',  sector, 'POST');
    if (errors) {
      setError(errors);
    } else {
      handleSave();
    }
  }

  const onDeleteSector = async (sector: ProductData) => {
    const {errors, data} = await fetchData('/products/delete',  { id: sector.id }, 'POST');
    if (errors) {
      setError(errors);
    } else {
      handleSave();
    }
  }

  return (
    <Modal
      open={open}
      // onClose={handleClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={{ 
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80%',
        maxWidth: '750px',
        bgcolor: 'background.paper',
        border: '1px solid #909894',
        boxShadow: '0px 4px 4px 0px #00000040',
        borderRadius:'16px',
        p: 4,
        maxHeight: '90%',
        overflow: 'auto',
       }}>
        <Typography id="modal-title" variant="h4" component="h2" fontWeight="600">
          { product.id ? "Edit Product SKU" : "Add Product SKU" }
        </Typography>
        <Stack mt={4} spacing={4}>
          <FormGroup>
            <Typography fontWeight="600">Name</Typography>
            <TextField
              variant="outlined"
              value={mProduct.name}
              error={error.name ? true : false}
              helperText={error?.name?.[0] ?? ''}
              onChange={(e) => setMProduct({ ...mProduct, name: e.target.value })}
              sx={{ width: '100%' }}
            />
          </FormGroup>
          <FormGroup>
            <Typography fontWeight="600">SKU</Typography>
            <TextField
              variant="outlined"
              value={mProduct.sku}
              error={error.sku ? true : false}
              helperText={error?.sku?.[0] ?? ''}
              onChange={(e) => setMProduct({ ...mProduct, sku: e.target.value })}
              sx={{ width: '100%' }}
            />
          </FormGroup>
          <FormGroup>
            <FormControl error={error.category_id ? true : false}>
              <Typography fontWeight="600">Category</Typography>
              <Select 
                native
                variant="outlined"
                value={mProduct.category_id ?? ''}
                onChange={(e) => setMProduct({ ...mProduct, category_id: e.target.value })}
                sx={{ width: '100%' }}
              >
                <option></option>
                {
                  categories.map((item: any, index: number) => (
                    <>
                      <CategorySelectOption key={index} item={item} depth={0} />
                    </>
                  ))
                }
              </Select>
              {
                error.category_id ? (
                  <FormHelperText color="#ff1744" style={{ marginBottom: "-20px" }}>
                    {error?.category_id?.[0] ?? ''}
                  </FormHelperText>
                ) : null
              }
            </FormControl>
          </FormGroup>
          <FormGroup>
            <Stack direction='row' spacing={2}>
              <Button variant="contained" size="large" onClick={() => onSaveSector(mProduct)}>
                Save Product SKU
              </Button>
              {
                product.id && (
                  <Button variant="outlined" size="large" color="error" onClick={() => 
                    openDialog(
                      "Confirm Deletion",
                      `Are you sure you want to delete the client "${mProduct.name}"?`,
                      () => onDeleteSector(mProduct)
                    )
                  }>
                    Delete Product SKU
                  </Button>
                )
              }
              <Button variant="text" size="large" onClick={handleClose}>
                Cancel
              </Button>
            </Stack>
          </FormGroup>
        </Stack>
        <ConfirmDialog
          open={isOpen}
          title={config.title}
          description={config.description}
          onConfirm={config.onConfirm}
          onClose={closeDialog}
        />
      </Box>
    </Modal>
  )
}
